/**
 * Declarative config of « Mes contrats » (`extranet.contractsListCard` —
 * fiches 22087 / 22089 / 22986): stable columns, the status dot renderer,
 * the toolbar (search `q` + status filter `etat`, built with the shared
 * `buildListToolbar`) and the DataList config builder (endpoint mode on
 * `boby-ws2-ea-tab-polices`).
 *
 * The cell renderer and the row-click key are registered once on import with
 * the singleton `dataListWorkflowRegistry` (desktop rows and the < 768px
 * mobile cards share them).
 */

import {
  dataListWorkflowRegistry,
  type ColumnConfig,
  type DataListConfig,
  type FilterConfig,
} from '@components/integrated/data-list';

import type { ContractStatusOption } from '../../api/contractsService';
import { EMPTY_STATE_TEXT } from '../shared/cardText';
import { EXTRANET_DATE_RENDERER } from '../shared/cellRenderers';
import {
  buildListToolbar,
  buildStatusFilter,
  statusFilterOptions,
  type ListStatusFilterSpec,
} from '../shared/listToolbarConfig';
import { ContractStatusCell } from './ContractStatusCell';

/** Registered id of the list card. */
export const CONTRACTS_LIST_CARD_ID = 'extranet.contractsListCard';

/**
 * Texts of the list card (labels live in config, never inline JSX). No base
 * title: the page header carries « Mes contrats » (a `setTitle` rule adds a
 * card title). Placeholder of the 22986 mockup.
 */
export const CONTRACTS_LIST_TEXT = {
  searchPlaceholder: 'Rechercher par n°, produit, assuré…',
  statusFilterLabel: 'Statut',
  allStatuses: 'Tous les statuts',
  errorTitle: 'Impossible de charger les contrats.',
} as const;

/** Status selected on load (fiche 22089: « par défaut positionné sur EC »). */
export const CONTRACTS_DEFAULT_STATUS = 'EC';

/**
 * Value of the « Tous les statuts » option: empty ⇒ `etat` is not sent (the
 * shared `ALL_OPTION_VALUE`; a literal so fast refresh sees a constant).
 */
export const ALL_STATUSES_VALUE = '';

/** Renderer key of the status column. */
export const CONTRACT_STATUS_RENDERER = 'extranet-contractStatus';

/** Row-click handler key of the list (registered by the card with its navigate closure). */
export const CONTRACTS_LIST_ROW_OPEN_HANDLER = 'extranet-contractsListRowOpen';

// Status (lib_situation) — dotted badge + tooltip (`./ContractStatusCell`).
dataListWorkflowRegistry.registerCellRenderer(CONTRACT_STATUS_RENDERER, (value) => (
  <ContractStatusCell value={value} />
));

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

/** Stable column keys (fiche labels `contrats.liste.ligne.*`). */
export type ContractsListColumnKey =
  | 'assure'
  | 'produit'
  | 'numero'
  | 'assureur'
  | 'dateEffet'
  | 'statut';

/** Base columns (design order). VEOS has no sort parameter ⇒ not sortable. */
export const CONTRACTS_LIST_COLUMNS: Readonly<Record<ContractsListColumnKey, ColumnConfig>> = {
  assure: { label: 'Assuré', order: 1, type: 'text', className: 'font-medium', sortable: false },
  produit: { label: 'Produit', order: 2, type: 'text', sortable: false },
  numero: {
    label: 'N° contrat',
    order: 3,
    type: 'text',
    className: 'font-mono text-xs',
    sortable: false,
  },
  assureur: { label: 'Assureur', order: 4, type: 'text', sortable: false },
  dateEffet: {
    label: "Date d'effet",
    order: 5,
    type: 'custom',
    typeOptions: { renderer: EXTRANET_DATE_RENDERER },
    sortable: false,
  },
  statut: {
    label: 'Statut',
    order: 6,
    type: 'custom',
    typeOptions: { renderer: CONTRACT_STATUS_RENDERER },
    align: 'center',
    headerTooltip: 'Statut du contrat : en cours ou résilié',
    sortable: false,
  },
};

/** Column key → VEOS field of a `WS2_EA_TAB_POLICES` row. */
export const CONTRACTS_LIST_FIELD_MAPPING: Readonly<Record<ContractsListColumnKey, string>> = {
  assure: 'NOM2_PER',
  produit: 'LIB_PROD',
  numero: 'NUM_POL',
  assureur: 'NOM_CIE',
  dateEffet: 'DTEFF_POL',
  statut: 'lib_situation',
};

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

/** Stable id of the status filter (rule key of the `filters` bucket). */
export const CONTRACTS_STATUS_FILTER_ID = 'statut';

/**
 * The status filter options: the VEOS options (`VAL` / `LIB`, API order)
 * followed by « Tous les statuts » (empty value ⇒ no `etat` param).
 */
export function contractStatusFilterOptions(
  statusOptions: readonly ContractStatusOption[],
): Array<{ value: string; label: string }> {
  return statusFilterOptions(statusOptions, CONTRACTS_LIST_TEXT.allStatuses, 'last');
}

/** Spec of the status select (`etat`, « Tous les statuts » last), defaulting to `defaultStatus`. */
function contractsStatusSpec(
  statusOptions: readonly ContractStatusOption[],
  defaultStatus: string,
): ListStatusFilterSpec {
  return {
    id: CONTRACTS_STATUS_FILTER_ID,
    label: CONTRACTS_LIST_TEXT.statusFilterLabel,
    allLabel: CONTRACTS_LIST_TEXT.allStatuses,
    allPosition: 'last',
    paramName: 'etat',
    options: statusOptions,
    defaultValue: defaultStatus,
  };
}

/** The status filter (`etat`), defaulting to `defaultStatus`. */
export function buildContractsStatusFilter(
  statusOptions: readonly ContractStatusOption[],
  defaultStatus: string,
): FilterConfig {
  return buildStatusFilter(contractsStatusSpec(statusOptions, defaultStatus));
}

// ---------------------------------------------------------------------------
// Config builder
// ---------------------------------------------------------------------------

export interface ContractsListConfigArgs {
  /** Proxy URL of `boby-ws2-ea-tab-polices`; `null` (no VEOS environment) ⇒ inline empty list. */
  endpoint: string | null;
  statusOptions: readonly ContractStatusOption[];
  /** Status selected on load (`''` ⇒ « Tous les statuts »). */
  defaultStatus: string;
}

/**
 * Base DataList config of the list.
 *
 * Endpoint mode: DataList builds the request from its own state — search →
 * `q` (omitted when empty), status filter → `etat` (omitted for « Tous les
 * statuts »), plus `page=1&perPage=9999` (every contract on one page, no
 * footer — the design has no pagination). Sorting is off (no VEOS sort
 * parameter); rows keep the API order.
 */
export function buildContractsListConfig({
  endpoint,
  statusOptions,
  defaultStatus,
}: ContractsListConfigArgs): DataListConfig {
  return {
    id: CONTRACTS_LIST_CARD_ID,
    dataSource:
      endpoint === null
        ? { data: [] }
        : {
            endpoint: { erpEndpoint: endpoint, method: 'GET' },
            responseMapping: { itemsPath: 'data.data', totalPath: 'data.total' },
            fieldMapping: { ...CONTRACTS_LIST_FIELD_MAPPING },
            rowIdField: 'ID_NPOL',
          },
    columns: { ...CONTRACTS_LIST_COLUMNS },
    display: { hoverable: true, striped: true },
    sorting: { enabled: false },
    pagination: {
      mode: 'pagination',
      pageSize: 9999,
      pageParam: 'page',
      pageSizeParam: 'perPage',
      showPageSize: false,
    },
    toolbar: buildListToolbar({
      search: { placeholder: CONTRACTS_LIST_TEXT.searchPlaceholder, paramName: 'q' },
      status: contractsStatusSpec(statusOptions, defaultStatus),
    }),
    actions: { rowClick: { handler: CONTRACTS_LIST_ROW_OPEN_HANDLER } },
    i18n: {
      emptyMessage: EMPTY_STATE_TEXT,
      errorMessage: CONTRACTS_LIST_TEXT.errorTitle,
      searchPlaceholder: CONTRACTS_LIST_TEXT.searchPlaceholder,
    },
  };
}
